import type { InfiniteData } from "@tanstack/react-query";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  AssistantConversationListItemDto,
  AssistantConversationListItemDtoPaginationResultDto,
} from "@/api/types";
import { AskNobiChatFeed } from "@/components/AskNobi/AskNobiChatFeed";
import { AskNobiChatInput } from "@/components/AskNobi/AskNobiChatInput";
import { AskNobiChatInputNotice } from "@/components/AskNobi/AskNobiChatInputNotice";
import { AskNobiNegativeFeedbackModal } from "@/components/AskNobi/AskNobiNegativeFeedbackModal";
import { AskNobiSuggestionButton } from "@/components/AskNobi/AskNobiSuggestionButton";
import { useAskNobiChat } from "@/components/AskNobi/helpers/useAskNobiChat";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ThumbsDownIcon } from "@/components/Icons/ThumbsDownIcon";
import { ThumbsUpIcon } from "@/components/Icons/ThumbsUpIcon";
import { List } from "@/components/List/List";
import { Modal } from "@/components/Modal/Modal";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import { hasFilterableProjects } from "@/components/ProjectFilter/hasFilterableProjects";
import { ProjectLogo } from "@/components/ProjectLogo/ProjectLogo";
import { Button } from "@/design-system/Button";
import { Heading } from "@/design-system/Heading";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Text } from "@/design-system/Text";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useBool } from "@/hooks/useBool";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useElementHeight } from "@/hooks/useElementHeight";
import { askNobiChatMutations } from "@/queries/ask-nobi/mutations";
import { useAskNobiChatQueries } from "@/queries/ask-nobi/queryOptions";
import { QUERY_KEYS } from "@/query-keys";
import { Trans, useTranslation } from "@/translations";
import { RuntimeBottomPadding } from "@/ui-adapters/RuntimeBottomPadding";

import { TextTooltip } from "../../../../components/TextTooltip/TextTooltip";
import { AskNobiChatFilterDrawer } from "./components/AskNobiChatFilterDrawer";
import { AskNobiChatHeader } from "./components/AskNobiChatHeader";
import { AskNobiChatSidebar } from "./components/AskNobiChatSidebar";
import { AI_CREDITS_DAILY_LIMIT } from "./helpers/constants";
import { useDailyCredits } from "./helpers/useDailyCredits";

export function AskNobiChatPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t("page.ask-nobi-chat.title"));
  const postHog = usePostHog();

  const queryClient = useQueryClient();
  const askNobiChatQueries = useAskNobiChatQueries();

  const { data: linkedProjects = [] } = useLinkedProjects();

  const [isDesktopSidebarExpanded, desktopSidebarHandlers] = useBool(true);
  const [isMobileSidebarOpen, mobileSidebarHandlers] = useBool();

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const handleConversationCreated = useCallback(
    (conversationId: string, firstMessage: string) => {
      queryClient.setQueryData<InfiniteData<AssistantConversationListItemDtoPaginationResultDto>>(
        QUERY_KEYS.ASSISTANT_CONVERSATIONS_LIST(),
        (old) => {
          if (!old) return old;

          const now = new Date().toISOString();
          const newConversation = {
            id: conversationId,
            title: firstMessage,
            createdAt: now,
            updatedAt: now,
            canDelete: true,
          };

          return {
            ...old,
            pages: old.pages.map((page, index) =>
              index === 0 ? { ...page, items: [newConversation, ...page.items], total: page.total + 1 } : page,
            ),
          };
        },
      );
    },
    [queryClient],
  );
  const chat = useAskNobiChat({
    onConversationCreated: handleConversationCreated,
  });

  const filteredProjects = useMemo(
    () =>
      filterProjectsByFilters(linkedProjects, {
        owner: chat.filters.owners,
        maintenance: chat.filters.maintainers,
        city: chat.filters.cities,
        projectIds: chat.filters.projectIds,
      }),
    [linkedProjects, chat.filters],
  );
  const canFilter = useMemo(() => hasFilterableProjects(linkedProjects), [linkedProjects]);
  const activeFilterCount = [chat.filters.owners, chat.filters.maintainers, chat.filters.cities].filter(
    (v) => v && v.length > 0,
  ).length;

  const {
    data: conversationsData,
    isPending: isConversationsLoading,
    fetchNextPage: fetchMoreConversations,
    hasNextPage: hasMoreConversations,
    isFetchingNextPage: isFetchingMoreConversations,
  } = useInfiniteQuery(askNobiChatQueries.list());
  const conversations = useMemo(
    () => conversationsData?.pages.flatMap((page) => page.items) ?? [],
    [conversationsData],
  );

  const hasChatted = chat.messages.length > 0;
  const credits = useDailyCredits();

  const FORCED_FEEDBACK_THRESHOLD = 5;
  type FeedbackState = "visible" | "submitted" | "dismissed";
  const [feedbackState, setFeedbackState] = useState<FeedbackState>("visible");
  const [enrichmentModalOpen, enrichmentModalOpenHandlers] = useBool();
  const rateConversation = askNobiChatMutations.useRateConversation();
  const deleteConversation = askNobiChatMutations.useDeleteConversation();
  const { componentProps: deleteConversationModalProps, openDeleteModal: openDeleteConversationModal } =
    useDeleteModal<AssistantConversationListItemDto>("delete-nobi-conversation-modal");

  const { conversationId, loadConversation } = chat;
  const nextConversationRatedRef = useRef(false);
  useEffect(() => {
    setFeedbackState(nextConversationRatedRef.current ? "dismissed" : "visible");
    nextConversationRatedRef.current = false;
    enrichmentModalOpenHandlers.setFalse();
  }, [conversationId, enrichmentModalOpenHandlers]);

  useEffect(() => {
    if (feedbackState !== "submitted") {
      return;
    }
    const timer = setTimeout(() => setFeedbackState("dismissed"), 5000);

    return () => clearTimeout(timer);
  }, [feedbackState]);

  const userMessageCount = chat.messages.filter((m) => m.sender === "user").length;
  const showFeedbackBanner =
    !!conversationId && feedbackState === "visible" && userMessageCount >= FORCED_FEEDBACK_THRESHOLD;

  async function handleBannerRate(rating: "positive" | "negative") {
    if (!conversationId) {
      return;
    }
    if (rating === "negative") {
      enrichmentModalOpenHandlers.setTrue();
    }
    await rateConversation.mutateAsync({ conversationId, data: { rating } });
    if (rating === "positive") {
      setFeedbackState("submitted");
    }
  }

  const [inputValue, setInputValue] = useState("");
  const [isProjectsModalOpen, setIsProjectsModalOpen] = useState(false);
  const [loadingConversationId, setLoadingConversationId] = useState<string>();
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [footerRef, footerHeight] = useElementHeight<HTMLDivElement>();
  const sendMessage = chat.sendMessage;

  const handleSend = useCallback(async () => {
    const message = inputValue.trim();
    if (message) {
      setInputValue("");
      credits.increment();
      await sendMessage(message);
    }
  }, [sendMessage, inputValue, credits]);

  const handleSelectSuggestion = useCallback(
    (prompt: string) => {
      setInputValue("");
      postHog.capture("clicked_ask_nobi_suggestion", { page: "ask_nobi_chat", suggestion: prompt });
      void sendMessage(prompt);
    },
    [sendMessage, postHog],
  );

  const handleSelectConversation = useCallback(
    async (id: string) => {
      if (id === conversationId) {
        return;
      }

      setLoadingConversationId(id);
      try {
        const conversation = await queryClient.fetchQuery(askNobiChatQueries.details(id)).then((x) => x.data);
        const messages = await queryClient
          .fetchQuery(askNobiChatQueries.conversationMessages(id))
          .then((x) => x.data.items);
        loadConversation(id, messages, conversation?.filters);

        // Scroll to bottom
        requestAnimationFrame(() => {
          chatScrollRef.current?.scrollTo(0, 0);
          inputRef.current?.focus();
        });
      } finally {
        setLoadingConversationId(undefined);
      }
    },
    [conversationId, loadConversation, queryClient, askNobiChatQueries],
  );

  const suggestions = [
    t("page.ask-nobi-chat.suggestion.1"),
    t("page.ask-nobi-chat.suggestion.2"),
    t("page.ask-nobi-chat.suggestion.3"),
  ] as const;

  const welcomeHeadings = [
    t("page.ask-nobi-chat.welcome-heading.1"),
    t("page.ask-nobi-chat.welcome-heading.2"),
    t("page.ask-nobi-chat.welcome-heading.3"),
    t("page.ask-nobi-chat.welcome-heading.4"),
    t("page.ask-nobi-chat.welcome-heading.5"),
  ] as const;

  const [welcomeHeading, setWelcomeHeading] = useState(() => getRandomHeading(welcomeHeadings));

  function startNewConversation() {
    chat.clearChat();
    setWelcomeHeading(getRandomHeading(welcomeHeadings.filter((heading) => heading !== welcomeHeading)));
    inputRef.current?.focus();
  }

  async function handleDeleteConversation(conversation: AssistantConversationListItemDto) {
    const isActiveConversation = conversation.id === conversationId;
    await deleteConversation.mutateAsync(conversation.id);

    postHog.capture("deleted_ask_nobi_conversation", {
      page: "ask_nobi_chat_sidebar",
      active: isActiveConversation,
    });

    if (isActiveConversation) {
      startNewConversation();
    }
  }

  const inputNotice = (() => {
    if (feedbackState === "submitted") {
      return (
        <AskNobiChatInputNotice
          variant="success"
          text={t("component.ask-nobi-chat.feedback-banner.submitted")}
          onClose={() => setFeedbackState("dismissed")}
        />
      );
    }
    if (showFeedbackBanner) {
      return (
        <AskNobiChatInputNotice
          variant="special"
          text={t("component.ask-nobi-chat.feedback-banner.question")}
          actions={
            <>
              <Button
                disabled={rateConversation.isPending}
                onClick={() => handleBannerRate("positive")}
                data-testid="ai-chat-feedback-banner-good-btn"
                size="sm"
                variant="ghostSecondary"
                icon={<ThumbsUpIcon />}
              >
                {t("component.ask-nobi-chat.feedback-banner.good")}
              </Button>
              <Button
                disabled={rateConversation.isPending}
                onClick={() => handleBannerRate("negative")}
                data-testid="ai-chat-feedback-banner-not-great-btn"
                size="sm"
                variant="ghostSecondary"
                icon={<ThumbsDownIcon />}
              >
                {t("component.ask-nobi-chat.feedback-banner.not-great")}
              </Button>
            </>
          }
        />
      );
    }
    if (credits.showCloseToLimitBar || credits.showExceededBar) {
      return (
        <AskNobiChatInputNotice
          variant="warning"
          text={
            <Trans
              i18nKey={
                credits.showCloseToLimitBar
                  ? "components.ask-ai.modal.question-input.credits-limit-notice"
                  : "components.ask-ai.modal.question-input.credits-exceeded-notice"
              }
              values={{
                limit: AI_CREDITS_DAILY_LIMIT,
              }}
              components={{
                hint: <TextTooltip content={t("component.ask-nobi-modal.credits.hint")} />,
              }}
            />
          }
          onClose={credits.showCloseToLimitBar ? credits.dismissCloseToLimitBar : credits.dismissExceededBar}
        />
      );
    }

    return undefined;
  })();

  return (
    <>
      <section className="relative flex h-full flex-col overflow-y-auto @md:h-screen @md:overflow-hidden @md:p-space-lg">
        <div className="relative flex min-h-full shrink-0 flex-col overflow-hidden bg-background-default @md:min-h-0 @md:flex-1 @md:shrink @md:rounded-xl">
          <AskNobiChatHeader
            projects={filteredProjects}
            activeFilterCount={activeFilterCount}
            canFilter={canFilter && !hasChatted}
            hasSidebarContent={hasChatted || (!isConversationsLoading && conversations.length > 0)}
            onOpenFilter={() => setIsFilterDrawerOpen(true)}
            onOpenProjectsModal={() => setIsProjectsModalOpen(true)}
            onOpenMobileSidebar={mobileSidebarHandlers.setTrue}
          />

          <div className="flex min-h-0 flex-1">
            <AskNobiChatSidebar
              hasChatted={hasChatted}
              conversations={conversations}
              activeConversationId={chat.conversationId}
              loadingConversationId={loadingConversationId}
              onNewConversation={startNewConversation}
              onSelectConversation={handleSelectConversation}
              onDeleteConversation={openDeleteConversationModal}
              isDeleteConversationDisabled={(id) =>
                deleteConversation.isPending ||
                loadingConversationId !== undefined ||
                (id === conversationId && chat.state !== "idle")
              }
              isDesktopExpanded={isDesktopSidebarExpanded}
              onToggleDesktopExpanded={desktopSidebarHandlers.toggle}
              isMobileOpen={isMobileSidebarOpen}
              onCloseMobile={mobileSidebarHandlers.setFalse}
              hasMoreConversations={hasMoreConversations}
              isFetchingMoreConversations={isFetchingMoreConversations}
              onLoadMoreConversations={fetchMoreConversations}
            />

            <main className="@container flex min-h-0 flex-1 flex-col">
              <div className="relative min-h-0 flex-1">
                <RuntimeBottomPadding padding={footerHeight}>
                  {(runtimePadding) => (
                    <div
                      {...runtimePadding}
                      ref={chatScrollRef}
                      className="absolute inset-0 flex scrollbar-transparent flex-col-reverse overflow-y-auto px-space-lg pt-space-lg"
                    >
                      {(loadingConversationId || isConversationsLoading) && (
                        <div className="flex flex-1 items-center justify-center">
                          <Spinner size="md" />
                        </div>
                      )}

                      {!loadingConversationId && !isConversationsLoading && hasChatted && (
                        <AskNobiChatFeed chat={chat} onSelectFollowUp={handleSelectSuggestion} />
                      )}

                      {!loadingConversationId && !isConversationsLoading && !hasChatted && (
                        <section className="flex flex-1 flex-col items-center justify-center gap-space-lg">
                          <Heading level={2} align="center">
                            {welcomeHeading}
                          </Heading>
                          <nav aria-label={welcomeHeading} className="flex w-nobi-suggestion flex-col gap-card-compact">
                            {suggestions.map((suggestion) => (
                              <AskNobiSuggestionButton
                                key={suggestion}
                                onClick={() => handleSelectSuggestion(suggestion)}
                              >
                                {suggestion}
                              </AskNobiSuggestionButton>
                            ))}
                          </nav>
                        </section>
                      )}
                    </div>
                  )}
                </RuntimeBottomPadding>

                <div ref={footerRef} className="absolute inset-x-0 bottom-0">
                  <footer className="relative flex w-full flex-col items-center gap-card-compact pt-space-sm">
                    {inputNotice && <div className="w-full max-w-sm">{inputNotice}</div>}
                    <div className="relative w-full">
                      <div aria-hidden className="pointer-events-none absolute inset-0">
                        <div className="absolute inset-0 nobi-footer-backdrop-soft" />
                        <div className="absolute inset-0 nobi-footer-backdrop-medium" />
                        <div className="absolute inset-0 nobi-footer-backdrop-strong" />
                        <div className="absolute inset-0 bg-linear-to-b from-transparent to-background-default/70" />
                      </div>
                      <div className="relative mx-auto flex w-full max-w-sm flex-col items-center gap-card-compact px-space-md pb-nobi-footer-end @md:px-0">
                        <AskNobiChatInput
                          ref={inputRef}
                          value={inputValue}
                          onChange={setInputValue}
                          onSubmit={handleSend}
                          disabled={chat.state !== "idle"}
                          placeholder={t("component.ask-nobi-modal.question-input.placeholder")}
                        />
                        <Text as="p" variant="overline" tone="secondary" align="center" overflow="wrap">
                          {t("page.ask-nobi-chat.disclaimer")}
                        </Text>
                      </div>
                    </div>
                  </footer>
                </div>
              </div>
            </main>
          </div>
        </div>
        <div aria-hidden className="h-page-bottom shrink-0 @md:hidden" />
      </section>

      <AskNobiChatFilterDrawer
        key={chat.conversationId}
        isOpen={isFilterDrawerOpen}
        onOpenChange={setIsFilterDrawerOpen}
        linkedProjects={linkedProjects}
        filters={{
          owner: chat.filters.owners,
          maintenance: chat.filters.maintainers,
          city: chat.filters.cities,
        }}
        onApply={(chatFilters) =>
          chat.changeFilters({
            owners: chatFilters.owner,
            maintainers: chatFilters.maintenance,
            cities: chatFilters.city,
          })
        }
      />

      <DeleteModal
        title={t("page.ask-nobi-chat.conversation-sidebar.delete.title")}
        description={t("page.ask-nobi-chat.conversation-sidebar.delete.description")}
        onDelete={handleDeleteConversation}
        deleteBtnProps={{
          text: t("page.ask-nobi-chat.conversation-sidebar.delete.action"),
          "data-testid": "delete-nobi-conversation-confirm",
        }}
        {...deleteConversationModalProps}
      />

      {conversationId && (
        <AskNobiNegativeFeedbackModal
          isOpen={enrichmentModalOpen}
          onClose={() => {
            enrichmentModalOpenHandlers.setFalse();
            setFeedbackState("submitted");
          }}
          conversationId={conversationId}
          onRated={() => setFeedbackState("submitted")}
        />
      )}

      <Modal.Root
        isOpened={isProjectsModalOpen}
        onOpenChange={setIsProjectsModalOpen}
        title={t("page.ask-nobi-chat.projects-popover.title")}
        size="sm"
        height={680}
      >
        <Modal.Body>
          <List items={filteredProjects} getKey={(project) => project.id}>
            {(project) => (
              <Inline gap="sm" align="center">
                <div className="size-control-md shrink-0 overflow-hidden rounded-sm">
                  <ProjectLogo src={project.logoUrl} />
                </div>
                <span className="truncate text-body-emphasis">{project.name}</span>
              </Inline>
            )}
          </List>
        </Modal.Body>
      </Modal.Root>
    </>
  );
}

function getRandomHeading(headings: readonly string[]): string {
  return headings[Math.floor(Math.random() * headings.length)];
}
